import React, { useRef, useState, useEffect, useMemo } from "react";
import { SlideInSelection } from "slideinselection";
import Enums from "./enums";
import SearchOutlined from "@material-ui/icons/SearchOutlined";
import Sort from "@material-ui/icons/Sort";
import PropTypes from "prop-types";
import AddressSortingAndFiltering from "../utils/addressSortingAndFiltering";
import SearchField from "./searchField";
import { unsafeCharRegex } from 'wbm-inputs';
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import AddressSortingAndFilteringPaging from "../utils/addressSortingAndFilteringPaging";
import { from } from "rxjs";
import { AddressList } from "./addressList";

const addressSort = "__AddressSort";

const WBMSelectedAddress = ({
  addresses,
  renderTrigger,
  selectedAddressId,
  manageAddressesUrl,
  onSelectedAddressChanged,
  showAvailableBudget,
  toggleAddAddressSelected,
  containerId,
  isAddressBudgetType,
  isSecondarySlideIn,
  displayDeliveryDisclaimer,
  storageService,
  currentAddress,
  usePaging,
  forceOpen,
  closeCallback,
  ...props
}) => {
  let SIS = useRef(null);

  const getSort = () => {
    if (storageService) {
      let item = storageService.getItem(addressSort);
      if (item) {
        return item;
      }
    }
    return Enums.SortOrder.None;
  };

  let [sort, changeSort] = useState(getSort());
  let [searchValue, setSearchValue] = useState("");
  let [sortedFilteredAddresses, setSortedFilteredAddresses] = useState(null);

  let [displaySortView, showSortView] = useState(false);
  let [displaySearchView, showSearchView] = useState(false);

  const pageSize = 100;

  let [numberOfAddressesRendered, setNumberOfAddressesRendered] = useState(0);

  useEffect(() => {
    if (usePaging) {
      setNumberOfAddressesRendered(pageSize);
    } else {
      const observable = from(
        AddressSortingAndFiltering.sortAndFilterAddresses(
          sort,
          searchValue,
          addresses,
          currentAddress
        )
      );

      const subscription = observable.subscribe(
        (sortedAndFilteredDeliveryAddresses) => {
          if (sortedAndFilteredDeliveryAddresses != null && !usePaging) {
            const getSelectedIndex = (x) => x.id == selectedAddressId;
            var selectedIndex =
              sortedAndFilteredDeliveryAddresses.findIndex(getSelectedIndex);
            if (selectedIndex > 0) {
              var selectedAddress = sortedAndFilteredDeliveryAddresses.splice(
                selectedIndex,
                1
              )[0];
              sortedAndFilteredDeliveryAddresses.unshift(selectedAddress);
            }
          }

          setSortedFilteredAddresses(sortedAndFilteredDeliveryAddresses);
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [addresses, sort, searchValue, usePaging, currentAddress]);

  useEffect(() => {
    if (usePaging) {
      const observable = from(
        AddressSortingAndFilteringPaging.sortAndFilterAddressesPaging(
          sort,
          searchValue,
          addresses,
          numberOfAddressesRendered,
          currentAddress
        )
      );

      const subscription = observable.subscribe(
        (sortedAndFilteredDeliveryAddresses) => {
          if (sortedAndFilteredDeliveryAddresses != null && !usePaging) {
            const getSelectedIndex = (x) => x.id == selectedAddressId;
            var selectedIndex =
              sortedAndFilteredDeliveryAddresses.findIndex(getSelectedIndex);
            if (selectedIndex > 0) {
              var selectedAddress = sortedAndFilteredDeliveryAddresses.splice(
                selectedIndex,
                1
              )[0];
              sortedAndFilteredDeliveryAddresses.unshift(selectedAddress);
            }
          }

          setSortedFilteredAddresses(sortedAndFilteredDeliveryAddresses);
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [
    numberOfAddressesRendered,
    addresses,
    sort,
    searchValue,
    usePaging,
    currentAddress,
  ]);

  useEffect(() => {
    if (sortedFilteredAddresses != null && !usePaging) {
      const getSelectedIndex = (x) => x.id == selectedAddressId;
      var selectedIndex = sortedFilteredAddresses.findIndex(getSelectedIndex);
      if (selectedIndex > 0) {
        const x = [...sortedFilteredAddresses];
        var selectedAddress = x.splice(selectedIndex, 1)[0];
        x.unshift(selectedAddress);
        setSortedFilteredAddresses(x);
      }
    }
  }, [sortedFilteredAddresses, selectedAddressId, usePaging]);

  const currentAddresses = useMemo(() => {
    return sortedFilteredAddresses ? sortedFilteredAddresses : addresses;
  }, [sortedFilteredAddresses, addresses]);

  const closeComponent = (addressId) => {
    if (onSelectedAddressChanged && addressId) {
      onSelectedAddressChanged(addressId);
      setSearchValue("");
    }

    SIS.current.slideItOut();
  };

  const sortChanged = (e) => {
    if (storageService) {
      storageService.setItem(addressSort, e);
    }

    changeSort(e);
    showSortView(false);
  };

  const onSortViewClick = (e) => {
    showSortView(!displaySortView);
  };

  const onSearchValueChange = (val) => {
    let pattern = unsafeCharRegex;
    let regex = new RegExp(pattern);
    let test = regex.test(val);

    if (!test) {
      setSearchValue(val);

      if (usePaging) {
        setNumberOfAddressesRendered(pageSize);
        if (document.getElementsByClassName("sis-body")[0]) {
          var myDiv = document.getElementsByClassName("sis-body")[0];
          myDiv.scrollTop = 0;
        }
      }
    }
  };
  const toggleAddAddressSelectedAndClose = () => {
    toggleAddAddressSelected();
    SIS.current.slideItOut();
  };

  const onSearchViewClick = () => {
    setSearchValue("");
    showSearchView(!displaySearchView);
  };

  const onSearchCancelClick = () => {
    onSearchValueChange("");
    showSearchView(false);
  };
  const searchAddresses = () => {
    showSearchView(false);
  };

  const onAddressSelect = () => {
    if (!displaySortView && !displaySearchView) {
      return true;
    }
    return false;
  };

  const isSecondarySlideinCheck = (isSecondarySlideIn) => {
    if (!isSecondarySlideIn && displaySortView) {
      return true;
    }

    return isSecondarySlideIn;
  };

  let renderSortOrFilterView = () => {
    if (displaySortView) {
      return <div className="sort-view">Sort</div>;
    } else if (displaySearchView) {
      return (
        <SearchField
          placeholder="Search For Address..."
          onSearchValueChange={onSearchValueChange}
          onSearchBackClick={onSearchViewClick}
          onSearchCancelClick={onSearchCancelClick}
          searchValue={searchValue}
          search={searchAddresses}
        />
      );
    }
    return (
      <div className="search-and-sort-view">
        <SearchOutlined onClick={onSearchViewClick} />
        <Sort onClick={onSortViewClick} />
      </div>
    );
  };

  return (
    <div className="shipping-address">
      <SlideInSelection
        forceOpen={forceOpen}
        onScrollBody={(event) => {
          if (usePaging) {
            let element = event.target;
            if (
              element.scrollHeight -
                (element.scrollTop + element.clientHeight) <=
              100
            ) {
              setNumberOfAddressesRendered(
                (numberOfAddressesRendered += pageSize)
              );
            }
          }
        }}
        ref={SIS}
        button={renderTrigger(closeComponent)}
        header={renderSortOrFilterView()}
        containerId={containerId}
        isSecondary={isSecondarySlideinCheck(isSecondarySlideIn)}
        isSecondarySlideIn={isSecondarySlideIn}
        onFirstSlideout={onAddressSelect()}
        closeCallback={() => {
          if (usePaging) {
            setNumberOfAddressesRendered(pageSize);
            if (document.getElementsByClassName("sis-body")[0]) {
              var myDiv = document.getElementsByClassName("sis-body")[0];
              myDiv.scrollTop = 0;
            }
          }

          if (closeCallback) {
            closeCallback();
          }

          setSearchValue("");
          showSortView(false);
          showSearchView(false);
        }}
        onSearchAddress={displaySearchView}
        {...props}
      >
        <div className="container">
          <AddressList
            addresses={currentAddresses}
            selectedAddressId={selectedAddressId}
            onDoneClick={closeComponent}
            displaySortView={displaySortView}
            manageAddressesUrl={manageAddressesUrl}
            sortOrder={sort}
            onSelectedSortClick={sortChanged}
            showAvailableBudget={showAvailableBudget && isAddressBudgetType}
            displayDeliveryDisclaimer={displayDeliveryDisclaimer}
            usePaging={usePaging}
          />

          {toggleAddAddressSelected !== null && !displaySortView && (
            <div className="align-center">
              <a href="#/main" onClick={toggleAddAddressSelectedAndClose}>
                <AddCircleOutlineIcon />
                New Address
              </a>
            </div>
          )}
        </div>
      </SlideInSelection>
    </div>
  );
};

WBMSelectedAddress.propTypes = {
  renderTrigger: PropTypes.func.isRequired,
  addresses: PropTypes.array.isRequired,
  selectedAddressId: PropTypes.string,
  manageAddressesUrl: PropTypes.string,
  onSelectedAddressChanged: PropTypes.func.isRequired,
  showAvailableBudget: PropTypes.bool,
  toggleAddAddressSelected: PropTypes.func,
  containerId: PropTypes.string,
  isAddressBudgetType: PropTypes.bool,
  isSecondarySlideIn: PropTypes.bool,
  forceOpen: PropTypes.bool,
  closeCallback: PropTypes.func,
};

WBMSelectedAddress.defaultProps = {
  selectedAddressId: 0,
  toggleAddAddressSelected: null,
  isSecondarySlideIn: false,
};

export default WBMSelectedAddress;
